import { Link } from 'react-router-dom';
import type { LeadListItem, ScoreBasis } from '@auctionradar/shared';
import { TierBadge } from './TierBadge';
import { RiskBadge } from './RiskBadge';
import { ZoneSvgThumb } from './ZoneSvgThumb';
import { fmtEur } from './SourceRow';
import { zoneNameEl } from '../lib/zones';

const pct = (n: number | null): string =>
  n == null ? '—' : `${Math.round(n * 100)}%`;

const fmtEurSqm = (n: number | null): string =>
  n == null ? '—' : `${fmtEur(n)}/m²`;

const floorLabel = (floor: number | null): string | null =>
  floor == null
    ? null
    : floor === 0
      ? 'ισόγειο'
      : floor < 0
        ? 'υπόγειο'
        : `${floor}ος`;

/** Compact "specific combo used" line — only the sources actually present. */
function basisCombo(basis: ScoreBasis): string {
  const parts: string[] = [];
  const add = (label: string, s: { value: number | null; count: number }) => {
    if (s.value != null) parts.push(`${label} ${fmtEur(s.value)}${s.count > 0 ? `·n${s.count}` : ''}`);
  };
  add('Spit', basis.sources.spitogatos);
  add('XE', basis.sources.xe);
  add('iA-R1', basis.sources.iauctionR1);
  add('iA-R3', basis.sources.iauctionR3);
  return parts.join(' · ');
}

export function LeadCard({ lead }: { lead: LeadListItem }) {
  const l2 = zoneNameEl(lead.zoneL2Slug);
  const l3 = zoneNameEl(lead.zoneSlug);
  const sqm = lead.sqm != null ? `${Math.round(lead.sqm)} τμ` : null;
  const floor = floorLabel(lead.floor);
  const auctionPerSqm =
    lead.startingPrice != null && lead.sqm != null && lead.sqm > 0
      ? lead.startingPrice / lead.sqm
      : null;
  const basis = lead.scoreBasis;

  return (
    <Link
      to={`/leads/${lead.id}`}
      className="block rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition hover:shadow-md"
    >
      {/* Header: tier · round · risk · iauction */}
      <div className="flex items-center gap-2">
        <TierBadge tier={lead.tier} />
        {lead.roundNumber != null && (
          <span className="text-xs text-slate-500">Γύρος {lead.roundNumber}</span>
        )}
        <span className="text-xs text-slate-300">·</span>
        <RiskBadge risk={lead.riskRating} />
        {lead.iauctionListingId != null && (
          <span
            className="ml-auto rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700"
            title={`iAuction #${lead.iauctionListingId}`}
          >
            iAuction
          </span>
        )}
      </div>

      {/* Location heading */}
      <div className="mt-2">
        <h3 className="truncate text-base font-semibold text-slate-900">
          {l3 ?? l2 ?? zoneNameEl(lead.municipality) ?? 'Άγνωστη περιοχή'}
        </h3>
        {l2 && l3 && l2 !== l3 && (
          <p className="truncate text-xs text-slate-500">{l2}</p>
        )}
      </div>

      {/* Thumb + property/price */}
      <div className="mt-2 flex gap-3">
        <ZoneSvgThumb
          zoneSlug={lead.zoneSlug}
          lat={lead.geoLat}
          lng={lead.geoLng}
          className="shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-slate-500">
            {[sqm, floor, lead.constructionYear].filter(Boolean).join(' · ') || '—'}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Δημοπρασία: {lead.auctionDate ?? '—'}
          </p>
          <div className="mt-1.5 flex items-baseline justify-between gap-2">
            <span className="text-base font-semibold text-slate-900">
              {fmtEur(lead.startingPrice)}
            </span>
            {lead.discountPct != null && (
              <span className="text-xs font-medium text-emerald-700">
                {pct(lead.discountPct)} έκπτωση
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Τιμή εκκίνησης: {fmtEurSqm(auctionPerSqm)}
          </p>
        </div>
      </div>

      {/* Valuation basis — the specific combo + price used to score it */}
      <div className="mt-2 rounded bg-slate-50 px-2 py-1.5">
        {basis ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                Αγορά (combo) · {basis.sqmBand} τμ
                {basis.yearBand && basis.yearBand !== '*' ? ` · ${basis.yearBand}` : ''}
              </span>
              <span className="text-sm font-semibold text-slate-900">
                {fmtEurSqm(basis.mid)}
              </span>
            </div>
            <p className="mt-0.5 truncate text-[11px] text-slate-500">
              {basisCombo(basis) || '—'}
            </p>
          </>
        ) : (
          <span className="text-[11px] text-slate-400">— χωρίς βάση τιμής</span>
        )}
      </div>
    </Link>
  );
}
